import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, Search, RefreshCw, Activity, Users,
  ChevronRight, Clock
} from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Injuries = () => {
  const { user } = useContext(AuthContext);
  const [activeSport, setActiveSport] = useState('mlb');
  const [injuries, setInjuries] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchInjuries = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/injuries`);
      setInjuries(response.data.by_sport || {});
      setLastUpdated(response.data.updated_at);
    } catch (error) {
      console.error('Error fetching injuries:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInjuries();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchInjuries, 300000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('out') || s.includes('ir')) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (s.includes('doubtful')) return 'bg-red-400/20 text-red-300 border-red-400/30';
    if (s.includes('questionable')) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    if (s.includes('probable')) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (s.includes('day-to-day')) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return 'bg-green-500/20 text-green-400 border-green-500/30';
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('out') || s.includes('ir')) return '🔴';
    if (s.includes('doubtful')) return '🔴';
    if (s.includes('questionable')) return '🟠';
    if (s.includes('probable')) return '🟡';
    if (s.includes('day-to-day')) return '⚪';
    return '🟢';
  };

  const getSportEmoji = (sport) => {
    switch(sport) {
      case 'nba': return '🏀';
      case 'nfl': return '🏈';
      case 'mlb': return '⚾';
      case 'fifa': return '⚽';
      case 'nhl': return '🏒';
      default: return '🎯';
    }
  };

  const getCurrentInjuries = () => {
    const sportInjuries = injuries[activeSport] || [];
    if (!searchQuery) return sportInjuries;
    
    const q = searchQuery.toLowerCase();
    return sportInjuries.filter(inj => 
      inj.player_name?.toLowerCase().includes(q) ||
      inj.team?.toLowerCase().includes(q) ||
      inj.injury_type?.toLowerCase().includes(q)
    );
  };

  const filteredInjuries = getCurrentInjuries();

  // Group by status
  const groupedByStatus = {
    critical: filteredInjuries.filter(i => ['out', 'injured reserve', 'ir', 'doubtful'].some(s => i.status?.toLowerCase().includes(s))),
    questionable: filteredInjuries.filter(i => i.status?.toLowerCase().includes('questionable')),
    probable: filteredInjuries.filter(i => i.status?.toLowerCase().includes('probable') || i.status?.toLowerCase().includes('day-to-day'))
  };

  const totalInjuriesAllSports = Object.values(injuries).reduce((acc, arr) => acc + (arr?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <AlertCircle className="h-8 w-8 text-orange-500" />
                Injury Report
              </h1>
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <Activity className="h-4 w-4 text-brand-lime" />
                Real-time injury updates
                {lastUpdated && (
                  <span className="text-xs">
                    • Updated {new Date(lastUpdated).toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
            <Button 
              onClick={fetchInjuries}
              variant="outline"
              className="border-[#1e3a5f] text-white hover:bg-[#112240]"
              disabled={refreshing}
              data-testid="refresh-injuries-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {['mlb'].map(sport => (
              <Card 
                key={sport}
                className={`bg-[#0f1e36] border-[#1e3a5f] cursor-pointer transition-all ${
                  activeSport === sport ? 'border-brand-cyan ring-1 ring-brand-cyan' : ''
                }`}
                onClick={() => setActiveSport(sport)}
                data-testid={`injury-stat-${sport}`}
              >
                <CardContent className="p-4 text-center">
                  <div className="text-2xl mb-2">{getSportEmoji(sport)}</div>
                  <div className="font-heading text-2xl font-bold text-white">
                    {injuries[sport]?.length || 0}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">
                    {sport} Injuries
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sport Tabs and Search */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Tabs value={activeSport} onValueChange={setActiveSport} className="flex-1">
              <TabsList className="bg-[#0f1e36] border border-[#1e3a5f] p-1">
                {['mlb'].map(sport => (
                  <TabsTrigger 
                    key={sport} 
                    value={sport}
                    className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-bold"
                    data-testid={`injury-tab-${sport}`}
                  >
                    {getSportEmoji(sport)} {sport}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search player or team..."
                className="pl-10 bg-[#0a1628] border-[#1e3a5f] text-white"
                data-testid="injury-search-input"
              />
            </div>
          </div>

          {/* Injury Counts */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Badge variant="outline" className="border-red-500 text-red-400">
              🔴 Out/IR: {groupedByStatus.critical.length}
            </Badge>
            <Badge variant="outline" className="border-orange-500 text-orange-400">
              🟠 Questionable: {groupedByStatus.questionable.length}
            </Badge>
            <Badge variant="outline" className="border-yellow-500 text-yellow-400">
              🟡 Probable/DTD: {groupedByStatus.probable.length}
            </Badge>
          </div>

          {/* Injury List */}
          {loading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-32 skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredInjuries.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]">
              <CardContent className="p-12 text-center">
                <Activity className="h-16 w-16 text-brand-lime mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">
                  No Injuries Reported
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery 
                    ? 'No injuries match your search' 
                    : `All ${activeSport.toUpperCase()} players are healthy!`
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Critical - Out/IR */}
              {groupedByStatus.critical.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-bold text-red-400 uppercase mb-4 flex items-center gap-2">
                    🔴 Out / Injured Reserve ({groupedByStatus.critical.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {groupedByStatus.critical.map((injury, idx) => (
                      <InjuryCard key={injury.id || idx} injury={injury} getStatusColor={getStatusColor} getStatusIcon={getStatusIcon} />
                    ))}
                  </div>
                </div>
              )}

              {/* Questionable */}
              {groupedByStatus.questionable.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-bold text-orange-400 uppercase mb-4 flex items-center gap-2">
                    🟠 Questionable ({groupedByStatus.questionable.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {groupedByStatus.questionable.map((injury, idx) => (
                      <InjuryCard key={injury.id || idx} injury={injury} getStatusColor={getStatusColor} getStatusIcon={getStatusIcon} />
                    ))}
                  </div>
                </div>
              )}

              {/* Probable/Day-to-Day */}
              {groupedByStatus.probable.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg font-bold text-yellow-400 uppercase mb-4 flex items-center gap-2">
                    🟡 Probable / Day-to-Day ({groupedByStatus.probable.length})
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {groupedByStatus.probable.map((injury, idx) => (
                      <InjuryCard key={injury.id || idx} injury={injury} getStatusColor={getStatusColor} getStatusIcon={getStatusIcon} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Injury Card Component
const InjuryCard = ({ injury, getStatusColor, getStatusIcon }) => (
  <Card className="bg-[#0f1e36] border-[#1e3a5f] card-hover">
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        {injury.headshot ? (
          <img 
            src={injury.headshot}
            alt={injury.player_name}
            className="w-14 h-14 rounded-full bg-[#112240] object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#112240] flex items-center justify-center">
            <Users className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-white truncate">{injury.player_name}</span>
            <span className="text-sm text-muted-foreground">{injury.position}</span>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <span className="text-brand-cyan text-sm font-medium">{injury.team}</span>
            {injury.team_full && (
              <span className="text-xs text-muted-foreground">• {injury.team_full}</span>
            )}
          </div>
          
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold border ${getStatusColor(injury.status)}`}>
            {getStatusIcon(injury.status)} {injury.status}
          </div>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-[#1e3a5f]">
        <div className="flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 text-orange-400" />
          <span className="text-white font-medium">{injury.injury_type}</span>
        </div>
        {injury.details && injury.details !== 'No details available' && (
          <p className="text-xs text-muted-foreground mt-1">{injury.details}</p>
        )}
        {injury.return_date && (
          <div className="flex items-center gap-1 text-xs text-brand-lime mt-2">
            <Clock className="h-3 w-3" />
            Expected return: {injury.return_date}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export default Injuries;
